// 任务快照沿用服务端数组优先和历史字符串兜底的计数口径。
export const MAX_TASK_LABELS = 900;

// 与 Java Character.isWhitespace 和 String.trim 的范围一致，两者不能互换。
function isJavaBlank(value) {
  return /^[\u0009-\u000d\u001c-\u001f\u0020\u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]*$/.test(value);
}

function javaTrim(value) {
  return value.replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '');
}

// Java 的旧名称串按竖线或英文逗号拆分，String.trim 不会去掉不换行空格或全角空格。
export function parseLegacyLabelNames(value) {
  if (typeof value !== 'string' || isJavaBlank(value)) return [];
  return value.split(value.includes('|') ? '|' : ',').map(javaTrim).filter(name => name !== '');
}

// JSON 数组选项先按 Java 空白规则过滤；逗号旧串先按 Java trim 处理，再去空项。
export function parseLegacyChoiceOptions(value, requireCertain = false) {
  if (!value) return [];
  if (typeof value !== 'string') throw new Error('invalid options');
  if (isJavaBlank(value)) return [];
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    // 服务端 JSON 解析器接受部分非标准数组；无法准确复现时不能用于认定快照未修改。
    if (requireCertain && /^[\u0000-\u0020]*\[/.test(value)) throw new Error('uncertain legacy options');
  }
  if (Array.isArray(parsed)) {
    if (requireCertain && parsed.some(option => option != null && typeof option !== 'string')) {
      throw new Error('uncertain legacy options');
    }
    return parsed.filter(option => option != null).map(option => String(option))
      .filter(option => !isJavaBlank(option)).map(javaTrim);
  }
  return value.split(',').map(javaTrim).filter(option => option !== '');
}

// 历史快照结构异常时不打开编辑弹窗，也不提交空值覆盖原数据。
export function hasInvalidTaskSnapshots(config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return true;
  if (config.labelSnapshots != null && !Array.isArray(config.labelSnapshots)) return true;
  const snapshots = [...(config.labelSnapshots || [])];
  if (config.labelSnapshot != null) snapshots.push(config.labelSnapshot);
  return snapshots.some((snapshot) => {
    if (snapshot == null) return false;
    if (typeof snapshot !== 'object' || Array.isArray(snapshot)) return true;
    if (snapshot.templateNameList != null && !Array.isArray(snapshot.templateNameList)) return true;
    if (snapshot.attrList != null && !Array.isArray(snapshot.attrList)) return true;
    return (snapshot.attrList || []).some((attr) => {
      if (!attr || typeof attr !== 'object' || Array.isArray(attr)) return true;
      if (attr.options != null && !Array.isArray(attr.options)) return true;
      if (attr.valueRange != null && typeof attr.valueRange !== 'string') return true;
      if (['RADIO', 'CHECKBOX'].includes(attr.controlType)
        && !(Array.isArray(attr.options) && attr.options.length)) {
        try { parseLegacyChoiceOptions(attr.valueRange, true); } catch (e) { return true; }
      }
      return false;
    });
  });
}

function snapshotLabelCount(snapshot) {
  if (!snapshot) return 0;
  const names = Array.isArray(snapshot.templateNameList)
    ? snapshot.templateNameList.map(name => name == null ? '' : String(name))
      .filter(name => !isJavaBlank(name)).map(javaTrim)
    : [];
  if (names.length) return new Set(names).size;
  const joined = typeof snapshot.templateName === 'string' ? snapshot.templateName : '';
  if (isJavaBlank(joined)) return 0;
  const separator = joined.includes('|') ? '|' : ',';
  return new Set(joined.split(separator).map(javaTrim).filter(name => name !== '')).size;
}

// 不跨模板去重：相同名称在不同快照中仍分别占用空间。
export function countTaskLabels(snapshots) {
  return (snapshots || []).reduce((total, snapshot) => total + snapshotLabelCount(snapshot), 0);
}

// 只归一已知的旧名称和选项存储形态；其余业务字段、未知字段与数组顺序均参与比对。
function comparisonSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) throw new Error('invalid snapshot');
  const result = { ...snapshot };
  const rawNames = snapshot.templateNameList;
  if (rawNames != null && (!Array.isArray(rawNames)
    || rawNames.some(name => name != null && typeof name !== 'string'))) throw new Error('invalid names');
  const names = Array.isArray(rawNames) && rawNames.some(name => name != null && !isJavaBlank(name))
    ? rawNames.slice() : parseLegacyLabelNames(snapshot.templateName);
  delete result.templateName;
  result.templateNameList = names;
  if (snapshot.attrList != null && !Array.isArray(snapshot.attrList)) throw new Error('invalid attrs');
  result.attrList = (snapshot.attrList || []).map((attr) => {
    if (!attr || typeof attr !== 'object' || Array.isArray(attr)) throw new Error('invalid attr');
    const normalized = { ...attr };
    delete normalized.optionsList;
    if (['RADIO', 'CHECKBOX'].includes(attr.controlType)) {
      if (attr.options != null && (!Array.isArray(attr.options)
        || attr.options.some(option => option != null && typeof option !== 'string'))) throw new Error('invalid options');
      let options = Array.isArray(attr.options) && attr.options.length ? attr.options.slice() : [];
      if (!options.length) options = parseLegacyChoiceOptions(attr.valueRange, true);
      normalized.options = options;
      delete normalized.valueRange;
    } else if (['NUMBER', 'TEXT'].includes(attr.controlType)
      && (attr.options == null || (Array.isArray(attr.options) && !attr.options.length))) {
      delete normalized.options;
    }
    return normalized;
  });
  return result;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableValue(value[key]);
    return result;
  }, Object.create(null));
}

// 已有任务未改快照时不改变历史处理规则。
export function sameTaskSnapshots(snapshots, config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)
    || (config.labelSnapshots != null && !Array.isArray(config.labelSnapshots))) return false;
  const original = config.labelSnapshots && config.labelSnapshots.length
    ? config.labelSnapshots.filter(Boolean) : (config.labelSnapshot ? [config.labelSnapshot] : []);
  if (!original.length) return false;
  try {
    const oldValues = original.map(comparisonSnapshot);
    const newValues = (snapshots || []).filter(Boolean).map(comparisonSnapshot);
    return JSON.stringify(stableValue(oldValues)) === JSON.stringify(stableValue(newValues));
  } catch (e) {
    return false;
  }
}
